import { useState, useEffect } from "react";
import { Calendar, Users, TrendingUp, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminOverview() {
  const [stats, setStats] = useState({
    total_appointments: 0,
    new_appointments: 0,
    confirmed_appointments: 0,
    by_source: []
  });
  const [recentAppointments, setRecentAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, appointmentsRes] = await Promise.all([
          axios.get(`${API}/admin/stats`),
          axios.get(`${API}/appointments`)
        ]);
        setStats(statsRes.data);
        setRecentAppointments(appointmentsRes.data.slice(0, 5));
      } catch (error) {
        console.error("Error fetching admin stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const statCards = [
    { 
      title: "Total Appointments", 
      value: stats.total_appointments, 
      icon: Calendar,
      color: "text-sky-600",
      bg: "bg-sky-50"
    },
    { 
      title: "New Requests", 
      value: stats.new_appointments, 
      icon: Clock,
      color: "text-amber-600",
      bg: "bg-amber-50"
    },
    { 
      title: "Confirmed", 
      value: stats.confirmed_appointments, 
      icon: TrendingUp,
      color: "text-green-600",
      bg: "bg-green-50"
    },
  ];

  return (
    <div className="p-6" data-testid="admin-overview">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900">Dashboard Overview</h1>
        <p className="text-slate-500">Welcome to the admin panel</p>
      </div>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {statCards.map((stat, i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">
                {stat.title}
              </CardTitle>
              <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">
                {loading ? "..." : stat.value}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Appointments */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Appointment Requests</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading...</div>
          ) : recentAppointments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Patient Name</th>
                    <th>Phone</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Service</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentAppointments.map((apt) => (
                    <tr key={apt.id}>
                      <td className="font-medium text-slate-900">{apt.patient_name}</td>
                      <td>{apt.phone}</td>
                      <td>{apt.preferred_date}</td>
                      <td>{apt.preferred_time}</td>
                      <td>{apt.service_type || "General"}</td>
                      <td>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium status-${apt.status}`}>
                          {apt.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              No appointments yet
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
